import React, { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useQuiz } from "../contexts/QuizContext";
import RateLimitModal from "./RateLimitModal";
import {
  RATE_LIMIT_FALLBACK_MESSAGE,
  extractRateLimitMessageFromResponse,
} from "../utils/rateLimit";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;
const GOAL_RATE_LIMIT_SUPPORT_MESSAGE =
  "You've reached the current limit for goal quizzes. Please wait a few minutes before trying again or contact support if you need a higher limit.";

// Custom icons based on subject to match the design style
const SubjectIcons = {
  Mathematics: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[#0D99FF]"
    >
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="6" x2="12.01" y2="6" />
      <line x1="12" y1="10" x2="12.01" y2="10" />
      <line x1="12" y1="14" x2="12.01" y2="14" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  ),
  Science: (
    // Microscope icon (similar to the design image)
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[#0D99FF]"
    >
      <path
        d="M6 18c-3.3137 0-6-2.6863-6-6s2.6863-6 6-6c1.0772 0 2.0838 0.2831 2.9555 0.7719L11 12l-2.0445 5.2281C8.0838 17.7169 7.0772 18 6 18z"
        transform="translate(6 3)"
      />
      <path d="M12 3v18" />
    </svg>
  ),
  English: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[#0D99FF]"
    >
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  "Computer Science": (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[#0D99FF]"
    >
      <path d="M14 9l-4 4-4-4" />
      <path d="M10 18H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-5" />
    </svg>
  ),
  History: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[#0D99FF]"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  Art: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[#0D99FF]"
    >
      <circle cx="5.5" cy="5.5" r="4.5" />
      <circle cx="18.5" cy="5.5" r="4.5" />
      <circle cx="12" cy="18.5" r="4.5" />
    </svg>
  ),
};

const OverallProgress = () => {
  const outletContext = useOutletContext();
  const period = outletContext?.period || "weekly"; // Default to weekly if not provided

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weeklyChallengeCount, setWeeklyChallengeCount] = useState(0);
  const [weeklyQuestionsAnswered, setWeeklyQuestionsAnswered] = useState(0);
  const [studyTime, setStudyTime] = useState("0hr 0min");
  const [dailyStreak, setDailyStreak] = useState(0);
  const [weeklyLoginDays, setWeeklyLoginDays] = useState([]);
  const [streakData, setStreakData] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const { saveQuizState } = useQuiz();
  const [startingGoalId, setStartingGoalId] = useState(null);
  const navigate = useNavigate();
  const [isRateLimitModalOpen, setIsRateLimitModalOpen] = useState(false);
  const [rateLimitServerMessage, setRateLimitServerMessage] = useState("");
  const showRateLimitModal = useCallback((message) => {
    setRateLimitServerMessage(message?.trim() || RATE_LIMIT_FALLBACK_MESSAGE);
    setIsRateLimitModalOpen(true);
  }, []);
  const handleRateLimitModalClose = useCallback(() => {
    setIsRateLimitModalOpen(false);
    setRateLimitServerMessage("");
  }, []);

  // Get period label for display
  const getPeriodLabel = () => {
    switch (period) {
      case "daily":
        return "Today";
      case "weekly":
        return "This Week";
      case "monthly":
        return "This Month";
      default:
        return "This Week";
    }
  };

  // Convert seconds to "Xhr Ymin" format
  const formatStudyTime = (seconds) => {
    if (!seconds || seconds === 0) return "0hr 0min";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}hr ${minutes}min`;
  };

  // Calculate daily streak from streak array
  const calculateDailyStreak = (streakArray) => {
    if (!Array.isArray(streakArray) || streakArray.length === 0) return 0;

    // Sort by date descending (most recent first)
    const sorted = [...streakArray].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );

    let streak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < sorted.length; i++) {
      const streakDate = new Date(sorted[i].date);
      streakDate.setHours(0, 0, 0, 0);

      const expectedDate = new Date(today);
      expectedDate.setDate(today.getDate() - i);

      if (
        streakDate.getTime() === expectedDate.getTime() &&
        sorted[i].count > 0
      ) {
        streak++;
      } else {
        break;
      }
    }

    return streak;
  };

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Map streak dates to weekly login days (for bubble display)
  const mapStreakToWeeklyLoginDays = (streakArray) => {
    if (!Array.isArray(streakArray)) return [];

    // Calculate Monday of this week
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 (Sun) - 6 (Sat)
    const mondayOffset = (dayOfWeek + 6) % 7; // Convert to 0 = Mon, 6 = Sun
    const monday = new Date(now);
    monday.setDate(now.getDate() - mondayOffset);
    monday.setHours(0, 0, 0, 0);

    // Get dates for this week (Monday to Sunday)
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      weekDates.push(formatDateLocal(date)); // Format as YYYY-MM-DD in local time
    }

    // Map streak data to week dates
    return weekDates
      .map((dateStr) => {
        const streakEntry = streakArray.find((s) => s.date === dateStr);
        return streakEntry && streakEntry.count > 0 ? dateStr : null;
      })
      .filter(Boolean);
  };

  useEffect(() => {
    const fetchProgressData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("Authentication required");
          setIsLoading(false);
          return;
        }

        const response = await axios.get(
          `${baseUrl}/progress/progress2/summary?period=${period}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = response.data || {};

        // Map effort data
        if (data.effort) {
          setWeeklyChallengeCount(data.effort.challenge_completed_count || 0);
          setWeeklyQuestionsAnswered(data.effort.question_answered_count || 0);
          setStudyTime(formatStudyTime(data.effort.study_time || 0));

          // Calculate daily streak
          if (data.effort.streak) {
            const streak = calculateDailyStreak(data.effort.streak);
            setDailyStreak(streak);
            setWeeklyLoginDays(mapStreakToWeeklyLoginDays(data.effort.streak));
            setStreakData(data.effort.streak);
          } else {
            setStreakData([]);
          }
        }

        // Map completed tasks
        if (Array.isArray(data.completed_task)) {
          setCompletedTasks(data.completed_task);
        } else {
          setCompletedTasks([]);
        }

        // Map pending tasks
        if (Array.isArray(data.pending_task)) {
          setPendingTasks(data.pending_task);
        } else {
          setPendingTasks([]);
        }
      } catch (err) {
        console.error("Error fetching progress data:", err);
        setError("Failed to load progress data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProgressData();
  }, [baseUrl, period]);

  // Function to get the correct icon, defaulting to a basic one if not found
  const getIcon = (subject) => SubjectIcons[subject] || SubjectIcons.Science;

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Process completed tasks to categorize them
  const processCompletedTasks = (tasks) => {
    if (!Array.isArray(tasks))
      return { quizTasks: [], teachmeTasks: [], solvedDoubtCount: 0 };

    const quizTasks = [];
    const teachmeTasks = [];
    let solvedDoubtCount = 0;

    tasks.forEach((task) => {
      if (task.attemptId) {
        // Quiz task with attemptId
        quizTasks.push(task);
      } else if (task.activity === "teachme") {
        // TeachMe task
        teachmeTasks.push(task);
      } else if (
        task.activity === "solveText" ||
        task.activity === "solveImageText"
      ) {
        // Solved doubt task - aggregate count
        solvedDoubtCount++;
      }
    });

    return { quizTasks, teachmeTasks, solvedDoubtCount };
  };

  const openPracticeQuiz = async (goal) => {
    const goalId = goal?._id || goal?.id || goal;
    if (!goalId) {
      console.error("handleStartGoal: Missing goal identifier", goal);
      setError("Unable to start this goal. Please try again.");
      return;
    }

    try {
      setStartingGoalId(goalId);
      setError(null);
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication required.");
        return;
      }

      const response = await fetch(
        `${baseUrl}/api/v2/start-goal-quiz/${goalId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      const rateLimitMessage = await extractRateLimitMessageFromResponse(
        response
      );
      if (rateLimitMessage) {
        const normalizedMessage =
          rateLimitMessage?.trim() || RATE_LIMIT_FALLBACK_MESSAGE;
        toast.warn(normalizedMessage);
        setError(normalizedMessage);
        showRateLimitModal(normalizedMessage);
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText?.trim() || "Failed to start quiz. Please try again."
        );
      }

      const rawPayload = await response.json();
      const resultPayload =
        rawPayload && typeof rawPayload === "object" && rawPayload.result
          ? rawPayload.result
          : rawPayload;

      const rawQuestionList = Array.isArray(resultPayload?.questions)
        ? resultPayload.questions
        : [];

      if (rawQuestionList.length === 0) {
        setError("No questions available for this goal.");
        return;
      }

      // Normalize questions: convert option objects to strings
      const normalizeOption = (option) => {
        if (typeof option === "string") return option;
        if (option && typeof option === "object") {
          return (
            option.option_text ||
            option.text ||
            option.label ||
            option.value ||
            String(option)
          );
        }
        return String(option || "");
      };

      const questionList = rawQuestionList.map((question) => {
        const normalizedQuestion = { ...question };

        // Normalize options array if it exists
        if (Array.isArray(normalizedQuestion.options)) {
          normalizedQuestion.options =
            normalizedQuestion.options.map(normalizeOption);
        }

        // Normalize choices array if it exists (some APIs use 'choices' instead of 'options')
        if (Array.isArray(normalizedQuestion.choices)) {
          normalizedQuestion.choices =
            normalizedQuestion.choices.map(normalizeOption);
        }

        return normalizedQuestion;
      });

      const questionCount = Number.isFinite(
        Number(resultPayload?.question_count)
      )
        ? Math.max(Number(resultPayload.question_count), questionList.length)
        : questionList.length;

      const timerSeconds =
        Number.isFinite(Number(resultPayload?.timer)) &&
        Number(resultPayload.timer) > 0
          ? Number(resultPayload.timer)
          : questionCount * 60;

      const userAnswersSeed = Array.from({ length: questionCount }, () => null);

      const normalizedResult = {
        ...resultPayload,
        questions: questionList,
        question_count: questionCount,
        timer: timerSeconds,
      };

      const storedQuestions =
        rawPayload && typeof rawPayload === "object" && rawPayload.result
          ? { ...rawPayload, result: normalizedResult }
          : normalizedResult;

      const goalDetailsPayload = {
        subject:
          goal?.subject ??
          normalizedResult?.subject ??
          normalizedResult?.extracted_content?.subject ??
          "",
        topic:
          goal?.topic ??
          normalizedResult?.topic ??
          normalizedResult?.extracted_content?.topic ??
          "",
        testDate: goal?.testDate ?? null,
        reminder: goal?.reminder ?? null,
        goalId,
      };

      saveQuizState({
        topicId: goalId,
        questions: storedQuestions,
        goalDetails: goalDetailsPayload,
        currentQuestionIndex: 0,
        selectedOption: null,
        score: 0,
        showExplanation: false,
        quizCompleted: false,
        leveledUp: false,
        goalStatus: goal?.status ?? "",
        userAnswers: userAnswersSeed,
      });

      navigate(`/practice/goalquestions/${goalId}`, {
        state: {
          attemptId: normalizedResult?.attempt_id,
          goalId,
          from: "overallProgress",
        },
      });
    } catch (error) {
      console.error("OverallProgress | Failed to start quiz:", error);
      const fallbackMessage =
        error?.message || "Failed to start quiz. Please try again.";
      toast.error(fallbackMessage);
      setError(fallbackMessage);
    } finally {
      setStartingGoalId(null);
    }
  };

  const rateLimitModal = (
    <RateLimitModal
      isOpen={isRateLimitModalOpen}
      serverMessage={rateLimitServerMessage}
      supportMessage={GOAL_RATE_LIMIT_SUPPORT_MESSAGE}
      onClose={handleRateLimitModalClose}
    />
  );

  if (isLoading) {
    return (
      <>
        <div className="min-h-screen sm:px-6 lg:px-8 font-sans pt-0">
          <div className="w-auto mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10 pt-0">
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-600">Loading progress data...</div>
            </div>
          </div>
        </div>
        {rateLimitModal}
      </>
    );
  }

  // if (error) {
  //   return (
  //     <>
  //       <div className="min-h-screen sm:px-6 lg:px-8 font-sans pt-0">
  //         <div className="w-auto mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10 pt-0">
  //           <div className="flex items-center justify-center py-12">
  //             <div className="text-red-600">{error}</div>
  //           </div>
  //         </div>
  //       </div>
  //       {rateLimitModal}
  //     </>
  //   );
  // }

  return (
    <>
      <div className="min-h-screen sm:px-6 lg:px-8 font-sans pt-0">
        <div className="w-auto mx-auto bg-white rounded-xl shadow-lg  p-6 sm:p-8 lg:p-10 pt-0">
          {/* Your Weekly Progress Section */}
          <div className="mb-12 pb-8 ">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 text-center sm:text-left">
              My Efforts
            </h2>
            {/* Responsive grid for weekly stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Challenges Completed Card */}
              <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center bg-white shadow-sm hover:shadow-md transition-shadow">
                <span className="text-gray-600 mb-2 text-base">
                  Challenges Completed
                </span>
                <span className="text-4xl font-extrabold pt-3 pb-3 text-[#0D99FF]">
                  {weeklyChallengeCount}
                </span>
                <span className="text-sm text-gray-500">
                  {getPeriodLabel()}
                </span>
              </div>

              {/* Questions Answered Card */}
              <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center bg-white shadow-sm hover:shadow-md transition-shadow">
                <span className="text-gray-600 mb-2 text-base">
                  Questions Answered
                </span>
                <span className="text-4xl font-extrabold pt-3 pb-3 text-[#0D99FF]">
                  {weeklyQuestionsAnswered}
                </span>
                <span className="text-sm text-gray-500">
                  {getPeriodLabel()}
                </span>
              </div>

              {/* Daily Streak Card */}
              <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center bg-white shadow-sm hover:shadow-md transition-shadow">
                <span className="text-gray-600 mb-2 text-base">
                  Daily Streak
                </span>
                <div className="flex flex-wrap justify-center gap-2 mb-3 mt-2">
                  {["M", "T", "W", "T", "F", "S", "S"].map((day, index) => {
                    // Calculate Monday of this week (local)
                    const now = new Date();
                    const dayOfWeek = now.getDay(); // 0 (Sun) - 6 (Sat)
                    const mondayOffset = (dayOfWeek + 6) % 7; // Convert to 0 = Mon, 6 = Sun
                    const monday = new Date(now);
                    monday.setDate(now.getDate() - mondayOffset);

                    // Compute date of this circle
                    const circleDate = new Date(monday);
                    circleDate.setDate(monday.getDate() + index);
                    circleDate.setHours(0, 0, 0, 0); // Normalize to start of day

                    // Check if the current circle's date is in the logged in days
                    // Use local date formatting to match streak data format
                    const circleDateStr = formatDateLocal(circleDate);
                    const isLoggedIn = weeklyLoginDays.includes(circleDateStr);

                    return (
                      <div
                        key={`${day}-${index}`}
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold
                        ${
                          isLoggedIn
                            ? "bg-[#0D99FF] text-white shadow-md"
                            : "bg-gray-200 text-gray-500"
                        }`}
                      >
                        {day}
                      </div>
                    );
                  })}
                </div>
                {(() => {
                  const activeDaysCount = streakData.filter(
                    (entry) => entry.count > 0
                  ).length;
                  return (
                    <div className="flex items-center justify-center gap-2 mt-3">
                      {/* {activeDaysCount > 1 && (
                        <span
                          className="text-[#0D99FF]"
                          role="img"
                          aria-label="star"
                        >
                          ⭐
                        </span>
                      )}
                      <span className="text-sm font-medium text-gray-700">
                        You were active on{" "}
                        <span className="text-[#0D99FF] font-bold">
                          {activeDaysCount}
                        </span>{" "}
                        out of last 7 days
                      </span>
                      {activeDaysCount > 1 && (
                        <span
                          className="text-[#0D99FF]"
                          role="img"
                          aria-label="star"
                        >
                          ⭐
                        </span>
                      )} */}
                    </div>
                  );
                })()}
              </div>

              {/* Study Time Card */}
              <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center bg-white shadow-sm hover:shadow-md transition-shadow">
                <span className="text-gray-600 mb-2 text-base">Study Time</span>
                <span className="text-4xl font-extrabold pt-3 pb-3 text-[#0D99FF]">
                  {studyTime || "0hr 0min"}
                </span>
                <span className="text-sm text-gray-500">
                  {getPeriodLabel()}
                </span>
              </div>
            </div>
          </div>
          {/* Level Up Progress Section */}
          {/* <div className="mb-12 border-t border-gray-200 pt-2">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 text-center sm:text-left">
            My Progress
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {progressData.levelUpProgress.map((item, index) => (
              <div
                key={index}
                className="flex flex-col bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-200"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="font-semibold text-gray-800 text-lg">
                    {item.topic}
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full border border-[#0D99FF] text-[#0D99FF] bg-purple-50 whitespace-nowrap">
                    {item.subject || "General"}
                  </span>
                </div>

                <div className="bg-gray-200 h-7 rounded-full relative mb-3 overflow-hidden">
                  <div
                    className="absolute inset-0 bg-[#0D99FF] rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${item.progress}%` }}
                  ></div>
                  <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-white">
                    {item.progress === 100
                      ? "Leveled Up!"
                      : `${item.progress}%`}
                  </div>
                </div>

                <div className="flex items-center text-sm mt-2 justify-center sm:justify-start">
                  <span className="text-gray-600 px-2 py-1 rounded-full bg-gray-100">
                    {item.previousLevel}
                  </span>
                  <svg
                    className="w-5 h-5 mx-2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                  <span className="text-purple-700 font-medium px-2 py-1 rounded-full bg-purple-100">
                    {item.newLevel}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div> */}

          {/* completed task */}
          <div class="mb-12 pt-8 pb-8 border-t border-gray-200">
            <div class="flex justify-between items-center mb-6 px-4 sm:px-0">
              <h2 class="text-2xl sm:text-3xl font-bold text-gray-800">
                My Completed Tasks{" "}
                <span role="img" aria-label="globe">
                  {/* 🌍 */}
                </span>
                {/* border-t border-gray-200 */}
              </h2>
              <button class="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium shadow-sm">
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                </svg>
                <span>Share</span>
              </button>
            </div>

            <div className="space-y-4">
              {(() => {
                const { quizTasks, teachmeTasks, solvedDoubtCount } =
                  processCompletedTasks(completedTasks);
                const hasAnyTasks =
                  quizTasks.length > 0 ||
                  teachmeTasks.length > 0 ||
                  solvedDoubtCount > 0;

                if (!hasAnyTasks) {
                  return (
                    <div className="border border-gray-200 rounded-xl p-6 flex items-center justify-center bg-white shadow-sm">
                      <p className="text-lg text-gray-500">
                        No completed tasks yet
                      </p>
                    </div>
                  );
                }

                return (
                  <>
                    {/* Quiz Tasks */}
                    {weeklyQuestionsAnswered > 0 && (
                      <div
                        className="relative overflow-hidden rounded-xl p-5 flex items-center justify-between border shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]"
                        style={{
                          backgroundColor: "rgba(13, 153, 255, 0.05)",
                          borderColor: "rgba(13, 153, 255, 0.2)",
                        }}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div
                            className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                            style={{
                              backgroundColor: "#0D99FF",
                            }}
                          >
                            <span
                              role="img"
                              aria-label="practice-icon"
                              className="text-2xl"
                            >
                              🧑‍🚀
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm md:text-base font-medium text-gray-800">
                              You completed{" "}
                              <span
                                className="font-bold text-lg"
                                style={{ color: "#FF6B00" }}
                              >
                                {weeklyQuestionsAnswered || 0}
                              </span>{" "}
                              questions
                            </p>
                          </div>
                        </div>
                        {/* <div
                          className="absolute top-0 right-0 w-32 h-32 rounded-full -mr-16 -mt-16"
                          style={{
                            backgroundColor: "rgba(13, 153, 255, 0.1)",
                          }}
                        ></div> */}
                      </div>
                    )}

                    {/* TeachMe Tasks */}
                    {teachmeTasks.slice(0, 3).map((task, index) => (
                      <div
                        key={task._id || index}
                        className="relative overflow-hidden rounded-xl p-5 flex items-center justify-between border shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]"
                        style={{
                          backgroundColor: "rgba(13, 153, 255, 0.05)",
                          borderColor: "rgba(13, 153, 255, 0.2)",
                        }}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div
                            className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                            style={{
                              backgroundColor: "#0D99FF",
                            }}
                          >
                            <span
                              role="img"
                              aria-label="teachme-icon"
                              className="text-2xl"
                            >
                              📚
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm md:text-base font-medium text-gray-800">
                              You learned{" "}
                              <span className="font-semibold text-gray-900">
                                {task.chapter || "Unknown"}
                              </span>{" "}
                              in{" "}
                              <span className="font-semibold text-gray-900">
                                {task.subject || "Unknown"}
                              </span>{" "}
                              using TeachMe
                            </p>
                          </div>
                        </div>
                        {/* <div
                          className="absolute top-0 right-0 w-32 h-32 rounded-full -mr-16 -mt-16"
                          style={{
                            backgroundColor: "rgba(13, 153, 255, 0.1)",
                          }}
                        ></div> */}
                      </div>
                    ))}

                    {/* Solved Doubts (Aggregated) */}
                    {solvedDoubtCount > 0 && (
                      <div
                        className="relative overflow-hidden rounded-xl p-5 flex items-center justify-between border shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]"
                        style={{
                          backgroundColor: "rgba(13, 153, 255, 0.05)",
                          borderColor: "rgba(13, 153, 255, 0.2)",
                        }}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div
                            className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                            style={{
                              backgroundColor: "#0D99FF",
                            }}
                          >
                            <span
                              role="img"
                              aria-label="solved-doubt-icon"
                              className="text-2xl"
                            >
                              ✅
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm md:text-base font-medium text-gray-800">
                              You solved{" "}
                              <span
                                className="font-bold text-lg"
                                style={{ color: "#0D99FF" }}
                              >
                                {solvedDoubtCount}
                              </span>{" "}
                              {solvedDoubtCount === 1 ? "doubt" : "doubts"}
                            </p>
                          </div>
                        </div>
                        {/* <div
                          className="absolute top-0 right-0 w-32 h-32 rounded-full -mr-16 -mt-16"
                          style={{
                            backgroundColor: "rgba(13, 153, 255, 0.1)",
                          }}
                        ></div> */}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Pending-tasks section */}
          <div className="space-y-4 pt-8 border-t border-gray-200">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 text-center sm:text-left">
              My Pending Tasks
            </h2>

            {/* Responsive grid for pending tasks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pendingTasks.length === 0 ? (
                <div className="col-span-2 border border-gray-200 rounded-xl p-6 flex items-center justify-center bg-white shadow-sm">
                  <p className="text-lg text-gray-500">No pending tasks</p>
                </div>
              ) : (
                pendingTasks.map((task, index) => {
                  const taskKey =
                    task?._id ||
                    `${task?.subject || "subject"}-${
                      task?.topic || index
                    }-${index}`;
                  const goalId = task?._id || task?.id || taskKey;
                  const isStartingGoal = startingGoalId === goalId;
                  return (
                    <div
                      key={task._id || index}
                      // Updated card styling: more rounded, deeper shadow (shadow-xl)
                      className="bg-white rounded-2xl shadow-xl p-6 space-y-4 border border-gray-100 transition-all duration-300 hover:shadow-2xl"
                    >
                      {/* 1. Icon, Subject, and Topic Stack */}
                      <div className="flex items-start space-x-3">
                        {/* Icon */}
                        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center pt-1">
                          {getIcon(task.subject)}
                        </div>

                        {/* Subject and Topic Stack */}
                        <div className="flex flex-col">
                          <h3 className="text-xl font-semibold text-gray-800">
                            {task.subject}
                          </h3>
                          <p className="text-lg font-medium text-gray-700 -mt-1">
                            {task.topic}
                          </p>
                        </div>
                      </div>

                      {/* 2. Due Date Text */}
                      {task.testDate && (
                        <p className="text-gray-500 text-sm pt-1">
                          Due date:{" "}
                          <span className="font-medium text-gray-700">
                            {formatDate(task.testDate)}
                          </span>
                        </p>
                      )}

                      {/* 3. Progress Bar (Simplified to match the new image) */}
                      {/* <div className="relative w-full h-1 mt-6 mb-2 px-1">
                  <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-300 transform -translate-y-1/2 rounded-full" />

                  {[0, 20, 40, 60, 80, 100].map((point, idx) => {
                      const taskProgress = task.progress || 0;
                      const isActive = taskProgress >= point;
                    const nextPoint = [20, 40, 60, 80, 100][idx];

                    return (
                      <React.Fragment key={idx}>
                        {idx > 0 && (
                          <div
                            className={`absolute h-0.5 transition-all duration-500 ${
                              isActive ? "bg-[#0D99FF]" : "bg-transparent"
                            }`}
                            // Position the line segment between the previous and current point
                            style={{
                              left: `${point - 20}%`, // Start 20% back
                              width: "20%", // Span 20%
                              zIndex: 5,
                              visibility:
                                  taskProgress > point - 20
                                  ? "visible"
                                  : "hidden",
                            }}
                          />
                        )}

                        <div
                          className={`w-3 h-3 rounded-full border-2 absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 cursor-default z-10
                            ${
                              isActive
                                ? "bg-[#0D99FF] border-[#0D99FF] shadow-md shadow-[#0D99FF]/50"
                                : "bg-white border-gray-300"
                            }`}
                          style={{ left: `${point}%` }} // Position dots precisely
                        />
                      </React.Fragment>
                    );
                  })}
                  </div> */}

                      {/* 4. Buttons with Gradient/Shadow */}
                      <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                        {/* Practice Now Button (Purple Gradient with strong shadow) */}
                        {/* <button
                          onClick={() => openPracticeQuiz(task)}
                          disabled={isStartingGoal}
                          className="flex-1 text-white rounded-xl py-3 text-base font-semibold transition-all duration-300
                               bg-gradient-to-r from-[#0D99FF] to-[#A08CFF] shadow-xl shadow-[#0D99FF]/50
                               hover:shadow-2xl hover:shadow-[#0D99FF]/70 active:translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {isStartingGoal ? "Preparing..." : "Practice Now"}
                        </button> */}

                        {/* Add to My Goals Button (Light Gradient/Soft Shadow) */}
                        <button
                          className="flex-1 rounded-xl py-3 text-base font-semibold transition-all duration-300
                               bg-gray-100 text-gray-700 border border-gray-200
                               shadow-xl shadow-gray-200/50 hover:bg-gray-200 active:translate-y-0.5"
                        >
                          Change Due Date
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
      {rateLimitModal}
    </>
  );
};

export default OverallProgress;
